import {useMemo} from "react";
import OrderIcon from "basicComponents/Icons/OrderIcon";
import {Grid, Typography} from "@material-ui/core";
import {makeStyles} from "@material-ui/core/styles";
import {FormattedMessage} from "react-intl";
import messages from "components/Message/messages";
import moment from "moment";
import CalendarIcon from "basicComponents/Icons/CalendarIcon";
import MessageIcon from "basicComponents/Icons/MessageIcon";
import NotificationIcon from "basicComponents/Icons/NotificationIcon";
import AdminMessages from "components/AdminOrderActionsModal/messages";
import GlobalMessages from "messages/messages";
import RefrezhButton from "components/RefrezhButton";
import patientDataMessages from "containers/PatientDataPage/messages";
import {withRouter} from "react-router";
import PropTypes from "prop-types";
import Message from "components/Message/index";

const useStyles = makeStyles(theme => ({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    "& > *:not(:first-child)": {
      marginLeft: theme.spacing(1),
    },
  },
  poDetails: {
    borderBottom: "1px solid rgba(0,0,0,0.46)",
    paddingBottom: theme.spacing(2),
  },
  patientButton: {
    marginTop: theme.spacing(1),
    marginLeft: "auto",
    marginRight: "auto",
  },
  messageBox: {
    border: "1px solid rgba(0,0,0,0.24)",
    borderColor: "rgba(0,0,0,0.24)",
  },
}));

function Base({
  activeMessage,
  shouldRenderPatient = false,
  history,
  onClose,
  children,
  title,
}) {
  const classes = useStyles();

  const messageIcon = useMemo(
    () => {
      switch (activeMessage.filterType) {
        case 2:
          return <OrderIcon />;
        case 3:
          return <CalendarIcon />;
        case 4:
          return <MessageIcon />;
        case 5:
          return <NotificationIcon />;

        default:
          return null;
      }
    },
    [activeMessage.filterType],
  );

  const renderPatient = useMemo(
    () => {
      const patient = activeMessage?.patient;

      if (!patient || !patient.id || !shouldRenderPatient) {
        return null;
      }

      const fields = [
        {
          label: <FormattedMessage {...AdminMessages.patient} />,
          value: patient.name,
        },
        {
          label: <FormattedMessage {...AdminMessages.patient_nr} />,
          value: patient.internalRef,
        },
        {
          label: <FormattedMessage {...AdminMessages.dentist} />,
          value: (patient.dentist || {}).name || "",
        },
        {
          label: <FormattedMessage {...messages.has_home_treatments} />,
          value: patient.homeShipping ? (
            <FormattedMessage {...GlobalMessages.yesButtonText} />
          ) : (
            <FormattedMessage {...GlobalMessages.noButtonText} />
          ),
        },
      ].filter(x => x.value);

      if (fields.length === 0) {
        return null;
      }

      return (
        <Grid key="patient-block" container className={classes.poDetails}>
          {fields.map(({ label, value }, i) => (
            <Grid key={`${i + 1}_${value}`} item xs={12} sm={6} md={3}>
              <Typography variant="caption">{label}</Typography>
              <Typography variant="subtitle1">{value}</Typography>
            </Grid>
          ))}
          <div className={classes.patientButton}>
            <RefrezhButton
              onClick={() => {
                history.push(`/patient/${patient.id}`);
              }}
            >
              <FormattedMessage {...patientDataMessages.previewInPatient} />
            </RefrezhButton>
          </div>
        </Grid>
      );
    },
    [
      classes.poDetails,
      history,
      classes.patientButton,
      shouldRenderPatient,
      activeMessage,
    ],
  );

  const header = useMemo(
    () => (
      <div className={classes.header}>
        <div className={classes.headerTitle}>
          {messageIcon}
          <Typography>{title}</Typography>
          {activeMessage.byUserName && (
            <Typography style={{ fontSize: "12px" }} color="textSecondary">
              <FormattedMessage
                {...messages.by_user_name}
                values={{ name: activeMessage.byUserName }}
              />
            </Typography>
          )}
        </div>
        <span>{moment.unix(activeMessage.updatedAt).format("DD-MM-YYYY")}</span>
      </div>
    ),
    [
      classes.headerTitle,
      activeMessage.byUserName,
      activeMessage.updatedAt,
      classes.header,
      messageIcon,
      title,
    ],
  );

  const body = useMemo(() => [renderPatient, children], [
    renderPatient,
    children,
  ]);

  return (
    <Message
      key="message"
      className={classes.messageBox}
      onClose={onClose}
      header={header}
      body={body}
    />
  );
}

Base.propTypes = {
  shouldRenderPatient: PropTypes.bool,
  history: PropTypes.any,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.any.isRequired,
  activeMessage: PropTypes.any,
};

export default withRouter(Base);
